import './AppleMaps.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { setGeolocation } from '../../../actions/search';

import constants, { themeOptions } from '../../../constants';
import companyConfigs from '../../../constants/company';
import { getFromLocalStorage } from '../../../utils/helpers';

const hostSpecificApiKeys = companyConfigs?.apiKeys?.appleMapsHostSpecificApiKeys;
let tokenID =
    hostSpecificApiKeys &&
    hostSpecificApiKeys.hasOwnProperty(window.location.hostname) &&
    hostSpecificApiKeys[window.location.hostname];
if (!tokenID) {
    tokenID = companyConfigs?.apiKeys?.appleMapsDefaultApiKey;
}
/**
 * # These utility functions provide various functionalities related to calculating coordinates,
 * initializing Mapkit, and providing default coordinates for different scenarios.
 */

// ## Extracts and returns coordinates from a place object
export const getCoordinatesFromPlace = (place) => {
    const coordinates = place.coordinates;
    const long = coordinates.longitude;
    const lat = coordinates.latitude;
    return [lat, long];
};

// ## Calculates the central coordinates from an array of coordinates
const calculateCentralCoordinates = (coordinates) => {
    return coordinates.reduce((accumulator, a) => accumulator + a, 0) / 3;
};

// ## Calculates and returns the center coordinates of a triangle formed by places
export const getCenterOfTriangle = (places) => {
    let longitudes = [];
    let latitudes = [];
    places.forEach((place) => {
        const [lat, long] = getCoordinatesFromPlace(place);
        longitudes.push(long);
        latitudes.push(lat);
    });
    const centerLat = calculateCentralCoordinates(latitudes);
    const centerLong = calculateCentralCoordinates(longitudes);
    return [centerLat, centerLong];
};

/**
 * ## Custom React hook for loading the Mapkit script.
 * - It encapsulates the logic for loading the Mapkit script asynchronously.
 * - It controls the loading status of the script, initializes the Mapkit and provides access to the Mapkit object for later use (e.g. to use the built-in reverse lookup to search for locations).
 */
export function useMapkitScript(language) {
    const src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';

    const statuses = {
        idle: 'idle',
        loading: 'loading',
        ready: 'ready',
        error: 'error',
    };

    const eventTypes = {
        load: 'load',
        error: 'error',
    };

    const [status, setStatus] = useState(src ? statuses.loading : statuses.idle);

    useEffect(
        () => {
            // Allow falsy src value if waiting on other data needed for
            // constructing the script URL passed to this hook.
            if (!src) {
                setStatus(statuses.idle);
                return;
            }
            // Fetch existing script element by src
            // It may have been added by another intance of this hook
            let script = document.querySelector(`script[src="${src}"]`);
            if (!script) {
                // Create script
                script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.setAttribute('data-status', statuses.loading);
                // Add script to document body
                document.body.appendChild(script);
                // Store status in attribute on script
                // This can be read by other instances of this hook
                const setAttributeFromEvent = (event) => {
                    script.setAttribute(
                        'data-status',
                        event.type === eventTypes.load ? statuses.ready : statuses.error,
                    );
                };
                script.addEventListener(eventTypes.load, setAttributeFromEvent);
                script.addEventListener(eventTypes.error, setAttributeFromEvent);
            } else {
                // Grab existing script status from attribute and set to state.
                setStatus(script.getAttribute('data-status'));
            }
            // Script event handler to update status in state
            // Note: Even if the script already exists we still need to add
            // event handlers to update the state for *this* hook instance.
            const setStateFromEvent = (event) => {
                if (event.type === eventTypes.load) {
                    initializeMapkit(window.mapkit, language ? language : 'en-EN');
                }
                setStatus(event.type === eventTypes.load ? statuses.ready : statuses.error);
            };
            // Add event listeners
            script.addEventListener(eventTypes.load, setStateFromEvent);
            script.addEventListener(eventTypes.error, setStateFromEvent);
            // Remove event listeners on cleanup
            return () => {
                if (script) {
                    script.removeEventListener(eventTypes.load, setStateFromEvent);
                    script.removeEventListener(eventTypes.error, setStateFromEvent);
                }
            };
        },
        // eslint-disable-next-line
        [src], // Only re-run effect if script src changes (for example, due to a language change)
    );

    return [status === statuses.ready, window.mapkit];
}

// ## Initializes Mapkit with authorization and language settings
export const initializeMapkit = (mapkit, language) => {
    mapkit.init({
        authorizationCallback: function (done) {
            done(tokenID);
        },
        language: language,
    });
};

// ## Default coordinates for fallback map location
export const fallbackCoordinates = {
    lat: 52.520007,
    long: 13.404954,
};

// ## Default coordinates for results not found situation
export const fallbackResultsNotFoundCoordinates = {
    lat: 13,
    long: -17,
    spanLat: 90,
    spanLong: 90,
};

const defaultRegionSpan = 0.031;
const defaultStreetSpan = 0.00357;

function AppleMap({
    className,
    language,
    places,
    modal,
    activeCard,
    mapOptions,
    clickHandler,
    theme,
    handleInteractiveMapLoad,
    responseTime,
    style,
    setGeolocation,
    userCoordinates,
    isDetailedViewModal,
    mapkit,
    resultsNotFound,
    resultsNotFoundCoordinates,
    hidePins,
    handleMapClick,
    zoomLevel,
    noAnnotationLabel,
    boundingRegion,
    defaultZoomLevel,
}) {
    let [map, setMap] = useState();
    const mapRef = useRef();
    const [mapWasSet, setMapWasSet] = useState(false);
    const [previousZoomLevel, setPreviousZoomLevel] = useState(0);
    const defaultSpan = useMemo(
        () => (defaultZoomLevel === constants.mapZoomLevel.street ? defaultStreetSpan : defaultRegionSpan),
        [defaultZoomLevel],
    );

    const getCurrentColorScheme = (_theme) => {
        let colorScheme = mapkit.Map.ColorSchemes.Light;
        if (_theme === constants.themeOptions[0].value) {
            colorScheme = mapkit.Map.ColorSchemes.Light;
        } else if (_theme === constants.themeOptions[1].value) {
            colorScheme = mapkit.Map.ColorSchemes.Dark;
        } else if (_theme === themeOptions[2].value) {
            const isDark = window.matchMedia('(prefers-color-scheme: dark)');
            if (isDark.matches) {
                colorScheme = mapkit.Map.ColorSchemes.Dark;
            } else {
                colorScheme = mapkit.Map.ColorSchemes.Light;
            }
        }

        return colorScheme;
    };

    const getRegion = useCallback(() => {
        if (!mapkit || !boundingRegion) {
            return;
        }

        const { latitude, longitude } = boundingRegion.center;
        const { latitudeDelta, longitudeDelta } = boundingRegion.span;

        if (!latitude || !longitude || !latitudeDelta || !longitudeDelta) {
            return;
        }

        return new mapkit.CoordinateRegion(
            new mapkit.Coordinate(latitude, longitude),
            new mapkit.CoordinateSpan(latitudeDelta * 10, longitudeDelta * 10),
        );
    }, [boundingRegion, mapkit]);

    // Run `reverseLookup` if user shares his coordinates
    useEffect(() => {
        if (navigator.geolocation && getFromLocalStorage('locationSetManually') !== 'true' && userCoordinates) {
            const geocoder = new mapkit.Geocoder({
                language: 'en-GB',
                getsUserLocation: true,
            });
            const position = userCoordinates;
            geocoder.reverseLookup(
                new mapkit.Coordinate(position.coords.latitude, position.coords.longitude),
                (err, data) => {
                    const results = data.results?.[0];
                    setGeolocation(
                        position.coords.longitude,
                        position.coords.latitude,
                        `${results.locality || results.administrativeArea}, ${results.country}`,
                    );
                },
            );
        }
    }, [modal, language, setGeolocation, userCoordinates, mapkit]);

    // Give annotations (markers) custom className and optionally display index number of the place
    const customAnnotationFactory = (coordinate, options) => {
        const div = document.createElement('p');
        if (!noAnnotationLabel) {
            div.textContent = options.data.index + 1;
        } else {
            div.textContent = '•';
        }
        div.className = `circle-annotation`;
        return div;
    };

    // Setup map
    useEffect(
        () => {
            let centerCoordinate, span, region;
            if (mapkit && mapRef.current && !mapWasSet) {
                // Prevent map re-load on every `places` change
                setMapWasSet(true);
                if (places?.length > 0) {
                    const getModalCenterCoordinate = () => {
                        return getCoordinatesFromPlace(places[0]);
                    };
                    const [centerLat, centerLong] = modal ? getModalCenterCoordinate() : getCenterOfTriangle(places);
                    centerCoordinate = new mapkit.Coordinate(centerLat, centerLong);
                    span = new mapkit.CoordinateSpan(defaultSpan, defaultSpan);
                    region = new mapkit.CoordinateRegion(centerCoordinate, span);
                } else {
                    // Set fallback map location in case there is no places provided
                    let lat, long;
                    const savedLocationForBusinessWidget = getFromLocalStorage('savedLocationForBusinessWidget');
                    if (userCoordinates) {
                        const position = userCoordinates;
                        lat = position.coords.latitude;
                        long = position.coords.longitude;
                    } else if (
                        savedLocationForBusinessWidget &&
                        savedLocationForBusinessWidget.hasOwnProperty('Latitude') &&
                        savedLocationForBusinessWidget.hasOwnProperty('Longitude')
                    ) {
                        lat = savedLocationForBusinessWidget.Latitude;
                        long = savedLocationForBusinessWidget.Longitude;
                    } else {
                        lat = fallbackCoordinates.lat;
                        long = fallbackCoordinates.long;
                    }

                    centerCoordinate = new mapkit.Coordinate(lat, long);
                    span = new mapkit.CoordinateSpan(defaultSpan, defaultSpan);
                    region = new mapkit.CoordinateRegion(centerCoordinate, span);
                }

                const newMap = new mapkit.Map(mapRef.current, {
                    region: region,
                    colorScheme: getCurrentColorScheme(theme),
                    ...mapOptions,
                });

                newMap._allowWheelToZoom = true;

                newMap.addEventListener('region-change-end', (event) => {
                    const time = Number(responseTime);
                    if (handleInteractiveMapLoad) {
                        if (time <= 1.2) {
                            handleInteractiveMapLoad();
                        } else {
                            setTimeout(() => {
                                handleInteractiveMapLoad();
                            }, Number(responseTime) * 5200);
                        }
                    }
                });

                newMap.addEventListener('single-tap', (event) => {
                    if (handleMapClick) {
                        handleMapClick(false);
                    }
                });

                newMap.addEventListener('select', (event) => {
                    const annotation = event.annotation;
                    if (modal) {
                        annotation.element.classList.add('selected');
                        newMap.showItems(event.annotation, { animate: true })
                    }
                    clickHandler(annotation.data.index);
                });
                newMap.addEventListener('deselect', (event) => {
                    const annotation = event.annotation;
                    if (modal) {
                        annotation.element.classList.remove('selected');
                    }
                    clickHandler(null);
                });

                setMap(newMap);
            }
        },
        // eslint-disable-next-line
        [
            mapRef,
            theme,
            mapWasSet,
            clickHandler,
            places,
            mapOptions,
            modal,
            handleInteractiveMapLoad,
            responseTime,
            mapkit,
        ],
    );

    // Clean up the map on unmount
    React.useEffect(() => {
        return () => {
            if (map) {
                map.destroy();
            }
        };
    }, [map]);

    // Set up annotations (pins)
    useEffect(() => {
        if (map && places?.length > 0 && !hidePins) {
            //TODO: investigate triggering on pin click
            if (isDetailedViewModal && map) {
                let region = getRegion();
                if (!region) {
                    const [lat, long] = getCoordinatesFromPlace(places[0]);
                    region = new mapkit.CoordinateRegion(
                        new mapkit.Coordinate(lat, long),
                        new mapkit.CoordinateSpan(defaultSpan, defaultSpan),
                    );
                }
                map.setRegionAnimated(region);
            }
            map.removeItems(map?.annotations);
            const markers = places.map((place, index) => {
                const [lat, long] = getCoordinatesFromPlace(place);
                const options = {
                    data: {
                        index: index,
                    },
                };
                const annotation = new mapkit.Annotation(
                    new mapkit.Coordinate(lat, long),
                    customAnnotationFactory,
                    options,
                );
                return annotation;
            });
            // showItems automatically shows and zooms into the chosen array of annotations
            // use addAnnotations or addItems to add annotations without focusing them
            if (!modal) {
                map.showItems(markers);
            } else {
                map.addAnnotations(markers);
            }
        } else if (map && hidePins) {
            map.removeItems(map?.annotations);
        }
        //eslint-disable-next-line
    }, [map, modal, places, mapkit, isDetailedViewModal, hidePins]);

    // Select pin on business card click
    useEffect(() => {
        if (places?.length && (activeCard || activeCard === 0)) {
            const annotation = map?.annotations[activeCard];
            if (map.cameraDistance > 900) {
                setPreviousZoomLevel(map.cameraDistance);
            }
            console.log(`Select pin on business card click`);
            if (annotation && !annotation.selected) {
                map.showItems(annotation, { animate: true})
                // let region = getRegion();
                // if (!region) {
                //     const [lat, long] = getCoordinatesFromPlace(places[activeCard]);
                //     //console.log(`defaultSpan `, defaultSpan);
                //     region = new mapkit.CoordinateRegion(
                //         new mapkit.Coordinate(lat, long),
                //         new mapkit.CoordinateSpan(
                //             defaultSpan,
                //             defaultSpan
                //         ),
                //     );
                // }
                // map.setRegionAnimated(region, true);
                annotation.selected = true;
            }
        } else if (activeCard === null && previousZoomLevel) {
             const annotation = map?.annotations.find(x => x.selected);
             if (annotation) {
                 annotation.selected = false;
             }
             map?.setCameraDistanceAnimated(previousZoomLevel)
        }
        //eslint-disable-next-line
    }, [activeCard, map, places, isDetailedViewModal, mapkit.Coordinate]);

    useEffect(() => {
        if (zoomLevel && map) {
            map?.setCameraDistanceAnimated(zoomLevel);
        }
    }, [zoomLevel, map]);

    useEffect(
        () => {
            if (map) {
                map.colorScheme = getCurrentColorScheme(theme);
            }
        },
        // eslint-disable-next-line
        [theme, map],
    );

    useEffect(
        () => {
            if (map && resultsNotFound) {
                const centerCoordinate = new mapkit.Coordinate(
                    resultsNotFoundCoordinates.lat,
                    resultsNotFoundCoordinates.long,
                );
                const span = new mapkit.CoordinateSpan(
                    resultsNotFoundCoordinates.spanLat,
                    resultsNotFoundCoordinates.spanLong,
                );
                const region = new mapkit.CoordinateRegion(centerCoordinate, span);
                map.setRegionAnimated(region);
            } else if (map && boundingRegion) {
                const region = getRegion();
                if (region) {
                    map.setRegionAnimated(region);
                }
            }
        },
        // eslint-disable-next-line
        [resultsNotFound, map, resultsNotFoundCoordinates, boundingRegion],
    );

    return (
        <div
            className={`${modal ? 'map-modal__' : ''}placeListMap${className ? ` ${className}` : ''}`}
            id={modal ? 'modal_map' : 'map'}
            ref={mapRef}
            style={style}
        />
    );
}

const mapStateToProps = (state) => ({
    userCoordinates: state.app.userCoordinates,
});

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            setGeolocation,
        },
        dispatch,
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(AppleMap);
